import { useState, useCallback } from "react";
import type { SavedMapPlotRequestDetail } from "@/features/map/types";

/**
 * プロット詳細画面で使用される複数のモーダルの開閉状態を管理するカスタムフック
 */
export const usePlotDetailModals = (): {
  openAddModal: boolean;
  setOpenAddModal: React.Dispatch<React.SetStateAction<boolean>>;
  openBulkEditModal: boolean;
  setOpenBulkEditModal: React.Dispatch<React.SetStateAction<boolean>>;
  openDeleteConfirmModal: boolean;
  setOpenDeleteConfirmModal: React.Dispatch<React.SetStateAction<boolean>>;
  openFilterModal: boolean;
  setOpenFilterModal: React.Dispatch<React.SetStateAction<boolean>>;
  openTagModal: boolean;
  setOpenTagModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedRowForEdit: SavedMapPlotRequestDetail | null;
  handleOpenAddModal: () => void;
  handleOpenBulkEditModal: () => void;
  handleOpenDeleteConfirmModal: () => void;
  handleOpenFilterModal: () => void;
  handleOpenTagModal: (
    id: number,
    tags: Array<{ id: number; name: string }>,
    details?: SavedMapPlotRequestDetail[]
  ) => void;
  handleCloseTagModal: () => void;
} => {
  // モーダルの開閉状態
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openBulkEditModal, setOpenBulkEditModal] = useState(false);
  const [openDeleteConfirmModal, setOpenDeleteConfirmModal] = useState(false);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [openTagModal, setOpenTagModal] = useState(false);

  // タグ編集モーダル用の選択行データ
  const [selectedRowForEdit, setSelectedRowForEdit] =
    useState<SavedMapPlotRequestDetail | null>(null);

  /**
   * 行追加モーダルを開く
   */
  const handleOpenAddModal = useCallback((): void => {
    setOpenAddModal(true);
  }, []);

  /**
   * 一括編集モーダルを開く
   */
  const handleOpenBulkEditModal = useCallback((): void => {
    setOpenBulkEditModal(true);
  }, []);

  /**
   * 削除確認モーダルを開く
   */
  const handleOpenDeleteConfirmModal = useCallback((): void => {
    setOpenDeleteConfirmModal(true);
  }, []);

  /**
   * フィルターモーダルを開く
   */
  const handleOpenFilterModal = useCallback((): void => {
    setOpenFilterModal(true);
  }, []);

  /**
   * タグ編集モーダルを開く
   */
  const handleOpenTagModal = useCallback(
    (
      id: number,
      tags: Array<{ id: number; name: string }>,
      details?: SavedMapPlotRequestDetail[]
    ): void => {
      const detail = details?.find((d) => d.id === id);
      if (detail) {
        setSelectedRowForEdit({
          ...detail,
          tags,
        });
        setOpenTagModal(true);
      }
    },
    []
  );

  /**
   * タグ編集モーダルを閉じる
   */
  const handleCloseTagModal = useCallback((): void => {
    setOpenTagModal(false);
    setSelectedRowForEdit(null);
  }, []);

  return {
    // モーダルの状態
    openAddModal,
    setOpenAddModal,
    openBulkEditModal,
    setOpenBulkEditModal,
    openDeleteConfirmModal,
    setOpenDeleteConfirmModal,
    openFilterModal,
    setOpenFilterModal,
    openTagModal,
    setOpenTagModal,
    selectedRowForEdit,

    // モーダル操作ハンドラ
    handleOpenAddModal,
    handleOpenBulkEditModal,
    handleOpenDeleteConfirmModal,
    handleOpenFilterModal,
    handleOpenTagModal,
    handleCloseTagModal,
  };
};
